import { useAppDispatch, useAppSelector } from "src/redux/redux-store/hooks";
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { setSelectedSession } from "src/redux/redux-store";
import { useChat } from "src/hooks/useChat";
import { ChatSession } from "src/interfaces/Chat";
import { useAuth } from "src/hooks/useAuth";
import { BaseSidebar } from "./BaseSidebar";
import { ChatSessionsList } from "src/components/Chat/ChatSessionsList";
import { ConfirmFeedbackModal } from "src/components/Modals/ConfirmationModal";
import { Box, Select, Skeleton, Text } from "@mantine/core";
import useParentChat from "src/hooks/useParentChat";
import { ChatBubbleOvalLeftIcon } from "@heroicons/react/24/outline";
import { usePayments } from "src/hooks/usePayment";

interface SideBarProps {
  opened: boolean;
}

const ParentChatSidebar = React.memo((props: SideBarProps) => {
  const dispatch = useAppDispatch();
  const chatSessions = useAppSelector((state) => state.chat.sessions);
  const selectedSession = useAppSelector((state) => state.chat.selectedSession);
  const { deleteSession } = useChat();
  const [visible, setVisible] = useState(false);
  const [selectedChat, setSelectedChat] = useState<string>("");
  const {
    levels,
    selectedLevel,
    subjects: parentSubjects,
    handleLevelChange,
    createNewSession,
  } = useParentChat();

  const sessionsLoading = useAppSelector((state) => state.chat.sessionsLoading);
  // Use the chat hook without arguments
  const chat = useChat();
  // Destructure the chat object to get the needed functions and state
  const { chatLoading } = chat;

  // New state for page loading
  const [pageLoading, setPageLoading] = useState(true);

  // Track when all initial data is loaded
  useEffect(() => {
    if (levels.length > 0 && !sessionsLoading) {
      setPageLoading(false);
    } else {
      setPageLoading(true);
    }
  }, [levels, sessionsLoading]);

  const groupedChats = useMemo(() => {
    if (pageLoading) return {};

    const now = new Date();
    const today = now.toDateString();
    const yesterday = new Date(now.setDate(now.getDate() - 1)).toDateString();

    return chatSessions.reduce<Record<string, ChatSession[]>>((acc, chat) => {
      const chatDate = new Date(chat.created_at);
      let group: string;

      if (chatDate.toDateString() === today) {
        group = "Today";
      } else if (chatDate.toDateString() === yesterday) {
        group = "Yesterday";
      } else {
        group = "Last Week";
      }

      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(chat);
      return acc;
    }, {});
  }, [chatSessions, pageLoading]);

  const selectSession = async (chatSession: ChatSession) => {
    try {
      // First set the selected session
      dispatch(setSelectedSession(chatSession));

      // Then fetch the messages for this session
      if (chatSession.id) {
        await chat.getMessages(chatSession.id);
      }
    } catch (error) {
      console.error("Error selecting session:", error);
    }
  };

  const handleDeleteChat = async (id: string) => {
    setSelectedChat(id);
    setVisible(true);
  };

  const handleNewSession = async () => {
    try {
      await createNewSession("New Session");
    } catch (error) {
      console.error("Error creating new session:", error);
    }
  };

  const handleConfirmDelete = async () => {
    if (selectedChat) {
      await deleteSession(selectedChat);
      setVisible(false);
    }
  };

  // Show skeleton loaders for 3 sessions (2 today, 1 yesterday) when loading
  const renderSkeletonLoaders = () => {
    return (
      <div className='space-y-4'>
        <div className='space-y-2'>
          <Skeleton height={20} width={80} radius='md' className='mb-2' />
          {[1].map((i) => (
            <div
              key={`today-${i}`}
              className='flex items-center p-2 rounded-lg hover:bg-gray-50'
            >
              <Skeleton height={24} width={24} radius='sm' className='mr-3' />
              <div className='flex-1'>
                <Skeleton
                  height={16}
                  width='80%'
                  radius='md'
                  className='mb-1'
                />
                <Skeleton height={12} width='60%' radius='md' />
              </div>
            </div>
          ))}
        </div>
        <div className='space-y-2'>
          <Skeleton height={20} width={100} radius='md' className='mb-2 mt-4' />
          <div className='flex items-center p-2 rounded-lg hover:bg-gray-50'>
            <Skeleton height={24} width={24} radius='sm' className='mr-3' />
            <div className='flex-1'>
              <Skeleton height={16} width='70%' radius='md' className='mb-1' />
              <Skeleton height={12} width='50%' radius='md' />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <BaseSidebar screenName='Home'>
      <Box>
        {/* Select dropdown with skeleton */}
        {pageLoading && levels.length > 0 ? (
          <div className='mb-4'>
            {/* <Skeleton height={20} width={120} radius='md' className='mb-1' /> */}
            <Skeleton height={40} radius='md' />
          </div>
        ) : (
          <Select
            key={selectedLevel || "no-level"}
            placeholder='Select grade level'
            value={selectedLevel}
            onChange={(value) => {
              if (value) {
                handleLevelChange(value);
              }
            }}
            data={levels.map((level) => ({
              value: level.id,
              label: level.name,
            }))}
            className='mb-4'
            styles={{
              input: {
                border: "1px solid #e2e8f0",
                borderRadius: "0.375rem",
                "&:focus": {
                  borderColor: "#3b82f6",
                  boxShadow: "0 0 0 1px #3b82f6",
                },
              },
              label: {
                fontSize: "0.875rem",
                marginBottom: "0.5rem",
                fontWeight: 500,
                color: "#374151",
              },
            }}
          />
        )}

        {(pageLoading ||
          (selectedLevel &&
            parentSubjects.length > 0 &&
            (chatLoading || sessionsLoading))) &&
        levels.length > 0 ? (
          <div className='p-2'>
            <Skeleton height={20} width={100} radius='md' className='mb-3' />
            {renderSkeletonLoaders()}
          </div>
        ) : Object.keys(groupedChats).length > 0 ? (
          <ChatSessionsList
            groupedChats={groupedChats}
            selectedSession={selectedSession?.id}
            onSelectSession={selectSession}
            onNewSession={handleNewSession}
            onDeleteSession={handleDeleteChat}
            maxHeight='calc(100vh - 560px)'
          />
        ) : (
          <div className='flex flex-col items-center justify-center py-8 text-center'>
            <ChatBubbleOvalLeftIcon className='w-12 h-12 text-gray-300 mb-4' />
            <Text className='text-gray-500 mb-2'>No chat sessions yet</Text>
            <Text className='text-sm text-gray-400'>
              Start a new chat to see it here
            </Text>
          </div>
        )}
      </Box>
      <ConfirmFeedbackModal
        title='Delete Chat'
        feedback='Are you sure you want to delete this chat?'
        confirmLabel='Delete Chat'
        visible={visible}
        onClose={() => setVisible(false)}
        onConfirm={handleConfirmDelete}
      />
    </BaseSidebar>
  );
});

export default ParentChatSidebar;
